/**
 * Where — the family location screen.
 *
 * Location is PUSH-based: each phone runs OwnTracks and reports itself straight to
 * the app (worker/routes/ingest.ts). Nothing polls a third party, so there is no
 * "feed is down" state — only "nobody's sharing yet", "sharing but no fix yet", and
 * the live list. The one honesty rule survives: never show an old position as if it
 * were live, so every card carries its age and a stale set gets a banner.
 *
 * Sharing is turned on per person by an adult in Settings → Family → Location. The
 * map itself needs a Mapbox token; without it the list still shows everyone.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiRequestError } from '../../lib/api';
import { useIdentity } from '../../lib/identity';
import type { LocationStatus, MemberLocation, MemberSummary } from '../../shared/types';
import FamilyMap from './FamilyMap';
import MemberCard from './MemberCard';
import { freshnessOf, relativeTime } from './format';

/**
 * GET /api/location/status returns the contract's `LocationStatus` plus the
 * roster (shared `MemberSummary` — no email, no token — with the one
 * location-specific bit, whether that person is sharing at all).
 */
type LocationRosterMember = MemberSummary & { linked: boolean; avatar_url: string | null };
type LocationStatusPayload = LocationStatus & { members: LocationRosterMember[] };

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

const REFETCH_MS = 60_000; // phones report on their own schedule; keep the page current
const TICK_MS = 30_000; // keeps "6 min ago" from going stale on a screen left open

export default function LocationPage() {
  const identity = useIdentity();
  const isAdult = identity.role === 'adult';

  const [locations, setLocations] = useState<MemberLocation[] | null>(null);
  const [status, setStatus] = useState<LocationStatusPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [mapBroken, setMapBroken] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  /**
   * Stable across the 30s "now" tick. An inline arrow here would change identity
   * on every tick and tear the map down with it.
   */
  const handleMapUnavailable = useCallback(() => setMapBroken(true), []);

  const load = useCallback(async () => {
    try {
      const [locs, st] = await Promise.all([
        api.get<MemberLocation[]>('/location'),
        api.get<LocationStatusPayload>('/location/status'),
      ]);
      setLocations(locs);
      setStatus(st);
      setError(null);
      setNow(Date.now());
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "Couldn't reach the house. Check your connection and try again."
      );
    }
  }, []);

  useEffect(() => {
    void load();
    const refetch = setInterval(() => void load(), REFETCH_MS);
    const tick = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      clearInterval(refetch);
      clearInterval(tick);
    };
  }, [load]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  // ---------------------------------------------------------------- loading
  if (!status || !locations) {
    if (error) return <ErrorState message={error} onRetry={() => void load()} />;
    return (
      <div className="space-y-4">
        <PageHeading />
        <div className="h-40 animate-pulse rounded-card border border-border bg-surface-sunk" />
        <div className="h-24 animate-pulse rounded-card border border-border bg-surface-sunk" />
        <span className="sr-only">Loading everyone’s location…</span>
      </div>
    );
  }

  const byMember = new Map(locations.map((l) => [l.member_id, l]));
  const sharing = status.members.filter((m) => m.linked);
  const notSharing = status.members.filter((m) => !m.linked);

  // ---------------------------------------------------------------- nobody sharing
  if (!status.configured) return <NotSharing isAdult={isAdult} />;

  // ---------------------------------------------------------------- the feed
  const newest = locations.reduce<string | null>(
    (acc, l) => (acc === null || l.recorded_at > acc ? l.recorded_at : acc),
    null
  );
  const everythingStale = newest !== null && freshnessOf(newest, now) === 'stale';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageHeading
          subtitle={
            newest
              ? `Last update ${relativeTime(newest, now)}.`
              : 'Sharing is on — waiting on the first location to arrive.'
          }
        />
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={refreshing}
          className="min-h-11 rounded-full border border-border bg-surface px-4 text-sm font-medium text-ink hover:bg-surface-sunk focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none disabled:opacity-60"
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <p className="rounded-card border border-clay/30 bg-clay-soft px-4 py-3 text-sm text-clay" role="alert">
          {error}
        </p>
      )}

      {everythingStale && (
        <div
          role="status"
          className="rounded-card border border-honey/40 bg-honey-soft px-4 py-3 text-sm text-ink"
        >
          <p className="font-medium text-honey">These positions are old.</p>
          <p className="mt-1 text-ink-soft">
            Showing the last known positions — nothing below is live. A phone may have OwnTracks
            paused, be out of signal, or have location turned off.
          </p>
        </div>
      )}

      {MAPBOX_TOKEN && !mapBroken && locations.length > 0 && (
        <FamilyMap
          locations={locations}
          members={status.members}
          token={MAPBOX_TOKEN}
          onUnavailable={handleMapUnavailable}
        />
      )}

      {!MAPBOX_TOKEN && (
        <p className="rounded-card border border-border bg-surface-sunk px-4 py-3 text-sm text-ink-soft">
          The map is off until a Mapbox token is set (<code className="text-ink">VITE_MAPBOX_TOKEN</code>). The list below has
          everything you need in the meantime.
        </p>
      )}

      {locations.length === 0 ? (
        <NoFixesYet isAdult={isAdult} refreshing={refreshing} onRefresh={() => void refresh()} />
      ) : (
        <ul className="space-y-3">
          {sharing.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              location={byMember.get(member.id)}
              now={now}
            />
          ))}
        </ul>
      )}

      {notSharing.length > 0 && (
        <p className="text-sm text-ink-faint">
          Not sharing location: {notSharing.map((m) => m.name).join(', ')}.{' '}
          {isAdult && (
            <Link to="/admin" className="font-medium text-sage underline underline-offset-2">
              Turn on sharing in Settings
            </Link>
          )}
        </p>
      )}

      <p className="text-xs text-ink-faint">
        Location comes straight from each person’s phone (OwnTracks) into this app — it never passes
        through anyone else’s servers. When a phone stops reporting, this page says the position is
        old rather than pretending it’s current.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- pieces

function PageHeading({ subtitle }: { subtitle?: string }) {
  return (
    <div>
      <h1 className="font-display text-2xl font-semibold text-ink">Where everyone is</h1>
      {subtitle && <p className="mt-1 text-sm text-ink-soft">{subtitle}</p>}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="space-y-4">
      <PageHeading />
      <div className="rounded-card border border-clay/30 bg-clay-soft p-6" role="alert">
        <p className="font-medium text-clay">We couldn’t load locations.</p>
        <p className="mt-1 text-sm text-ink-soft">{message}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 min-h-11 rounded-full bg-clay px-5 text-sm font-medium text-white hover:opacity-90 focus-visible:ring-2 focus-visible:ring-clay focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

/** Nobody has location sharing turned on yet. Point an adult at the setup. */
function NotSharing({ isAdult }: { isAdult: boolean }) {
  return (
    <div className="space-y-6">
      <PageHeading subtitle="No one is sharing location yet." />

      <div className="rounded-card border border-border bg-surface p-6">
        <h2 className="font-display text-lg font-semibold text-ink">
          Turn on location sharing
        </h2>
        <p className="mt-2 text-ink-soft">
          Each phone shares its own location straight into this app using the free, open-source
          OwnTracks app — nothing goes through a third party. Once someone’s sharing, they show up
          here with where they are, their battery, and whether they’re driving.
        </p>

        {isAdult ? (
          <>
            <ol className="mt-5 space-y-3 text-sm text-ink-soft">
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-sage-soft text-xs font-semibold text-sage">
                  1
                </span>
                <span>
                  Go to <strong className="text-ink">Settings → Family</strong> and press{' '}
                  <strong className="text-ink">Location</strong> next to a person.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-sage-soft text-xs font-semibold text-sage">
                  2
                </span>
                <span>
                  Turn on sharing and copy the private link it gives you into the OwnTracks app on
                  that phone. It starts reporting within a minute or two.
                </span>
              </li>
            </ol>
            <Link
              to="/admin"
              className="mt-6 inline-grid min-h-11 place-items-center rounded-full bg-sage px-5 text-sm font-medium text-white hover:opacity-90 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              Open Settings → Family
            </Link>
          </>
        ) : (
          <p className="mt-4 text-ink-soft">Ask a grown-up to turn it on.</p>
        )}
      </div>
    </div>
  );
}

/** Sharing is on for someone, but no position has arrived yet. */
function NoFixesYet({
  isAdult,
  refreshing,
  onRefresh,
}: {
  isAdult: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="rounded-card border border-border bg-surface p-6 text-center">
      <h2 className="font-display text-lg font-semibold text-ink">No positions yet</h2>
      <p className="mx-auto mt-2 max-w-md text-ink-soft">
        Sharing is on, so the first location should land here within a minute or two of OwnTracks
        being set up on the phone. Make sure the app has location permission set to “Always.”
      </p>
      {isAdult && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="mt-5 min-h-11 rounded-full bg-sage px-5 text-sm font-medium text-white hover:opacity-90 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-60"
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      )}
    </div>
  );
}
